// The Suvaani preferences, the same for both courses: course switch (members of both), Kids / Adults, explanations,
// transliteration, the pronunciation voice (device or Bhashini for Premium, with its speed), text size, motion and
// theme, plus "Show review labels" for editors, reviewers and admins. A course adds its own rows through `extra`
// (the Sanskrit sheet: Devanagari known, teacher captions). Shown in the header sheet of both courses, the mobile
// "More" sheet, the lesson player and the account page.

import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useTheme } from 'next-themes'
import { Baby, Crown, GraduationCap, Monitor, Moon, SlidersHorizontal, Sun, Volume2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePrefs } from '@/lib/client/prefs'
import { useAudio } from '@/prakrit/lib/client/audio'
import { canEdit, useSession } from '@/lib/client/session'
import { Switch } from '@/components/ui/switch'
import { CourseSwitch, courseOfPath } from '@/components/app/course-switch'

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'

/** Kids / Adults: one curriculum, two presentations, in both courses. */
export const ModeSwitch: FC<{ className?: string, compact?: boolean }> = ({ className }): ReactNode => {
    const { mode, setMode } = usePrefs()

    const option = (value: 'kids' | 'adults', label: string, icon: ReactNode) => (
        <button
            type='button'
            role='radio'
            aria-checked={mode === value}
            onClick={() => setMode(value)}
            className={cn(
                'inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition-colors',
                mode === value ? 'bg-card text-foreground shadow-sm ring-1 ring-border' : 'text-muted-foreground hover:text-foreground'
            )}
        >
            {icon}
            {label}
        </button>
    )

    return (
        <div role='radiogroup' aria-label='Kids or Adults' className={cn('flex gap-1 rounded-xl bg-secondary p-1', className)}>
            {option('kids', 'Kids', <Baby className='size-4' aria-hidden='true' />)}
            {option('adults', 'Adults', <GraduationCap className='size-4' aria-hidden='true' />)}
        </div>
    )
}

const Segmented: FC<{
    label: string
    value: string
    options: { value: string, label: ReactNode, disabled?: boolean }[]
    onChange: (value: string) => void
}> = ({ label, value, options, onChange }): ReactNode => (
    <div role='group' aria-label={label} className='grid grid-flow-col auto-cols-fr gap-1 rounded-xl bg-secondary p-1'>
        {options.map(o => (
            <button
                key={o.value}
                type='button'
                aria-pressed={value === o.value}
                disabled={o.disabled}
                onClick={() => onChange(o.value)}
                className={cn(
                    'inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg px-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                    value === o.value ? 'bg-card font-semibold shadow-sm ring-1 ring-border' : 'text-muted-foreground hover:text-foreground'
                )}
            >
                {o.label}
            </button>
        ))}
    </div>
)

/** One preference: a title, an optional help line and its control. */
export const PrefRow: FC<{ title: string, help?: ReactNode, children: ReactNode, inline?: boolean }> = ({ title, help, children, inline = false }): ReactNode => (
    <div className={cn('flex gap-3 border-b border-border py-4 first:pt-0 last:border-b-0 last:pb-0', inline ? 'items-center justify-between' : 'flex-col')}>
        <div className='flex flex-col gap-0.5'>
            <p className='text-sm font-semibold text-foreground'>{title}</p>
            {help && <p className='text-xs text-muted-foreground'>{help}</p>}
        </div>
        {children}
    </div>
)

const useBhashiniAvailable = (): boolean | null => {
    const [available, setAvailable] = useState<boolean | null>(null)

    useEffect(() => {
        let cancelled = false

        fetch('/api/tts')
            .then(res => (res.ok ? res.json() : { available: false }))
            .then((data: { available?: boolean }) => {
                if (!cancelled) setAvailable(data.available === true)
            })
            .catch(() => {
                if (!cancelled) setAvailable(false)
            })

        return () => {
            cancelled = true
        }
    }, [])

    return available
}

const VoiceRow: FC = (): ReactNode => {
    const prefs = usePrefs()
    const audio = useAudio()
    const { access } = useSession()
    const premium = access.prakrit || access.sanskrit
    const available = useBhashiniAvailable()
    const chosen = prefs.voice === 'bhashini' && premium ? 'bhashini' : 'device'

    return (
        <PrefRow
            title='Pronunciation voice'
            help='Who reads words aloud where there is no teacher recording.'
        >
            <Segmented
                label='Pronunciation voice'
                value={chosen}
                onChange={v => prefs.setVoice(v === 'bhashini' ? 'bhashini' : 'device')}
                options={[
                    { value: 'device', label: 'Device voice' },
                    {
                        value: 'bhashini',
                        disabled: !premium,
                        label: <><Crown className='size-3.5 text-saffron-strong' aria-hidden='true' />Bhashini</>
                    }
                ]}
            />
            {!premium && (
                <p className='text-xs text-muted-foreground'>
                    Bhashini&apos;s natural voices from Bharat come with Premium.{' '}
                    <Link href='/pricing' className='font-semibold text-saffron-strong hover:underline'>See Premium</Link>
                </p>
            )}
            {chosen === 'bhashini' && (
                <>
                    <Segmented
                        label='Bhashini voice'
                        value={prefs.voiceGender}
                        onChange={v => prefs.setVoiceGender(v === 'male' ? 'male' : 'female')}
                        options={[
                            { value: 'female', label: 'Female voice' },
                            { value: 'male', label: 'Male voice' }
                        ]}
                    />
                    {available === false && (
                        <p className='rounded-xl bg-saffron-soft px-3 py-2 text-xs text-foreground'>
                            Bhashini voices are being set up. Until then your device voice reads aloud.
                        </p>
                    )}
                </>
            )}
            {chosen === 'device' && audio.deviceVoices.length > 0 && (
                <select
                    value={prefs.deviceVoice ?? ''}
                    onChange={e => prefs.setDeviceVoice(e.target.value || null)}
                    className='h-10 w-full rounded-xl border border-input bg-card px-3 text-sm'
                    aria-label='Device voice'
                >
                    <option value=''>Automatic (best Devanagari voice)</option>
                    {audio.deviceVoices.map(v => <option key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</option>)}
                </select>
            )}
            {chosen === 'device' && audio.deviceVoices.length === 0 && (
                <p className='rounded-xl bg-muted px-3 py-2 text-xs text-muted-foreground'>No Devanagari voice was found on this device. Use the transliteration as your guide.</p>
            )}
            <label className='flex items-center gap-3 text-sm'>
                <span className='w-14 shrink-0 text-muted-foreground'>Speed</span>
                <input
                    type='range'
                    min={0.5}
                    max={1.2}
                    step={0.05}
                    value={prefs.speechRate}
                    onChange={e => prefs.setSpeechRate(Number(e.target.value))}
                    className='flex-1 accent-[var(--saffron-strong)]'
                    aria-label='Reading speed'
                />
                <span className='w-10 text-right tabular-nums'>{prefs.speechRate.toFixed(2)}</span>
                <button
                    type='button'
                    onClick={() => { void audio.speak('सुवाणी') }}
                    className='inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-sm font-semibold hover:bg-muted'
                >
                    <Volume2 className='size-4' aria-hidden='true' />
                    Test
                </button>
            </label>
            <label className='flex items-center justify-between gap-4 text-sm'>
                <span className='text-muted-foreground'>Read aloud where there is no recording</span>
                <Switch checked={audio.voiceEnabled} onCheckedChange={checked => audio.setVoiceEnabled(Boolean(checked))} aria-label='Read aloud where there is no recording' />
            </label>
        </PrefRow>
    )
}

export const SettingsPanel: FC<{ showMode?: boolean, extra?: ReactNode }> = ({ showMode = true, extra }): ReactNode => {
    const prefs = usePrefs()
    const { user } = useSession()
    const { theme, setTheme } = useTheme()
    const router = useRouter()
    const [mounted, setMounted] = useState(false)
    const sanskrit = courseOfPath(router.pathname) === 'sanskrit'

    useEffect(() => setMounted(true), [])

    return (
        <div className='flex flex-col'>
            <CourseSwitch className='border-b border-border pb-4' />

            {showMode && (
                <PrefRow title='Learner' help='Kids: big cards, simple words and stickers. Adults: full grammar notes.'>
                    <ModeSwitch />
                </PrefRow>
            )}

            <PrefRow title='Explanations in' help='Prakrit and Sanskrit always stay in Devanagari.'>
                <Segmented
                    label='Explanations in'
                    value={prefs.explain}
                    onChange={v => prefs.setExplain(v as 'hi' | 'en' | 'both')}
                    options={[
                        { value: 'hi', label: <span lang='hi' className='hindi'>हिन्दी</span> },
                        { value: 'en', label: 'English' },
                        { value: 'both', label: 'Both' }
                    ]}
                />
            </PrefRow>

            <PrefRow title='Transliteration' help={<span>Roman letters under every form, such as <span lang='pra-Latn' className='italic'>ahaṃ paḍhāmi</span>. Off still shows it in your first Sanskrit lessons.</span>} inline>
                <Switch checked={prefs.translit} onCheckedChange={checked => prefs.setTranslit(Boolean(checked))} aria-label='Show transliteration' />
            </PrefRow>

            <VoiceRow />

            <PrefRow title='Text size'>
                <Segmented
                    label='Text size'
                    value={String(prefs.textScale)}
                    onChange={v => prefs.setTextScale(Number(v))}
                    options={[
                        { value: '1', label: <span className='text-sm'>A</span> },
                        { value: '1.1', label: <span className='text-base'>A</span> },
                        { value: '1.25', label: <span className='text-lg'>A</span> }
                    ]}
                />
            </PrefRow>

            <PrefRow title='Animations' help='"Device" follows your device’s reduced-motion setting.'>
                <Segmented
                    label='Animations'
                    value={prefs.motion}
                    onChange={v => prefs.setMotion(v === 'reduce' || v === 'full' ? v : 'system')}
                    options={[
                        { value: 'system', label: 'Device' },
                        { value: 'reduce', label: 'Fewer' },
                        { value: 'full', label: 'All' }
                    ]}
                />
            </PrefRow>

            <PrefRow title='Theme' help={sanskrit ? 'The Sanskrit course always uses the light theme.' : undefined}>
                <Segmented
                    label='Theme'
                    value={mounted ? (theme ?? 'light') : 'light'}
                    onChange={v => setTheme(v)}
                    options={[
                        { value: 'light', label: <><Sun className='size-4' aria-hidden='true' /><span className='sr-only sm:not-sr-only'>Light</span></> },
                        { value: 'dark', label: <><Moon className='size-4' aria-hidden='true' /><span className='sr-only sm:not-sr-only'>Dark</span></> },
                        { value: 'system', label: <><Monitor className='size-4' aria-hidden='true' /><span className='sr-only sm:not-sr-only'>Device</span></> }
                    ]}
                />
            </PrefRow>

            {extra}

            {canEdit(user) && (
                <PrefRow title='Show review labels' help='Only for you; learners never see them.' inline>
                    <Switch checked={prefs.showReview} onCheckedChange={checked => prefs.setShowReview(Boolean(checked))} aria-label='Show review labels' />
                </PrefRow>
            )}
        </div>
    )
}

interface PreferencesSheetProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Course-specific rows, after the shared ones. */
    extra?: ReactNode
}

/** The preferences in a side sheet: what the header's preferences button opens in both courses. */
export const PreferencesSheet: FC<PreferencesSheetProps> = ({ open, onOpenChange, extra }): ReactNode => (
    <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side='right' className='w-full gap-0 overflow-y-auto bg-background sm:max-w-md'>
            <SheetHeader className='border-b border-border p-5'>
                <SheetTitle className='font-heading text-2xl'>Preferences</SheetTitle>
                <SheetDescription>The same in both courses. Saved on this device, and your mode and language also to your account.</SheetDescription>
            </SheetHeader>
            <div className='p-5'>
                <SettingsPanel extra={extra} />
            </div>
        </SheetContent>
    </Sheet>
)

/** The header's preferences button (both courses use the same icon and sheet). */
export const PreferencesButton: FC<{ onClick: () => void }> = ({ onClick }): ReactNode => (
    <button
        type='button'
        onClick={onClick}
        aria-label='Preferences'
        className='inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:border-saffron/50 hover:text-foreground'
    >
        <SlidersHorizontal className='size-5' aria-hidden='true' />
    </button>
)

/** The Prakrit header's preferences: the button and its sheet. */
export const SettingsMenu: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)

    return (
        <>
            <PreferencesButton onClick={() => setOpen(true)} />
            <PreferencesSheet open={open} onOpenChange={setOpen} />
        </>
    )
}
